import * as types from '@/store/mutations'
import { CAMPAIGN_LANDING_TYPES } from '@/utils/postAuthRedirect'

// TODO: comments, cleanup

const userModel = {
  id: false,
  name: false,
  slug: false,
  email: false,
  avatar: false,
  cpms: null,
  type: false,
  parent_user: null,
  publishers: null,
  is_impersonate: false,
  impersonate_user_type: null,
  max_campaigns: null,
  max_influencers: null,
  active_api: null,
  active_whitelabel: null,
  whitelabel_dashboard_id: null,
  active_ads: null,
  active_dashboard: null,
  active_content: null,
  active_analytics: null,
  timezone: 'UTC',
  global_message: {},
  main_message: {},
  policy: {},
  gam_network: null,

  demo_features: false,
  ai_admin_only: true,

  // The whitelabel access list. The legacy single-object `apps` key it replaced was
  // removed in PD-4870, once its last readers moved to an explicit app selector.
  apps_list: [],
}

// state
export const state = {
  ...userModel,
}

// getters
export const getters = {
  id: (state) => state.id,
  name: (state) => state.name,
  policy: (state) => state.policy,
  email: (state) => state.email,
  cpms: (state) => state.cpms,
  avatar: (state) => state.avatar,
  type: (state) => state.type,
  slug: (state) => state.slug,
  is_impersonate: (state) => state.is_impersonate,
  impersonate_user_type: (state) => state.impersonate_user_type,
  publishers: (state) => state.publishers,
  parent_user: (state) => state.parent_user,

  active_api: (state) => state.active_api,
  active_whitelabel: (state) => state.active_whitelabel,
  whitelabel_dashboard_id: (state) => state.whitelabel_dashboard_id,
  active_ads: (state) => state.active_ads,
  active_dashboard: (state) => state.active_dashboard,
  active_content: (state) => state.active_content,
  active_analytics: (state) => state.active_analytics,
  timezone: (state) => state.timezone,

  global_message: (state) => state.global_message,
  main_message: (state) => state.main_message,

  apps_list: (state) => state.apps_list,

  // PD-4862 — presence check across the access list. Replaces the old
  // `user/apps'].id` truthiness test, which only ever knew about one app.
  has_whitelabel_app: (state) => state.apps_list.length > 0,

  // Union of features across every whitelabel the user can access. This is the
  // non-campaign-scoped gating rule agreed in PD-4860: campaign context uses the
  // campaign's own app, everything else (sidebar, profiles, tags) uses the union.
  // Union is safe here because the backend check is already fail-open, so widening
  // cannot take a menu item away from anyone.
  apps_features: (state) =>
    state.apps_list.reduce((all, app) => {
      if (Array.isArray(app.features)) {
        app.features.forEach((feature) => {
          if (!all.includes(feature)) all.push(feature)
        })
      }

      return all
    }, []),
  gam_network: (state) => state.gam_network,

  demo_features: (state) => state.demo_features,
  ai_admin_only: (state) => state.ai_admin_only,

  show_ai_assistant: (state, _getters, _rootState, rootGetters) => {
    if (rootGetters['whitelabel/is_whitelabel']) return false
    if (state.is_impersonate) return true
    const allowedSlugs = [
      'admin',
      'brand-admin',
      'brand-employee',
      'agency',
      'agency-employee',
      'project-manager',
      'agency-client',
      'employee',
      'enterprise',
      'professional',
    ]
    return allowedSlugs.includes(state.type)
  },

  // PD-4676: user types that should land on the Campaigns section after authentication.
  // Any type not in this list keeps the previous default landing (the dashboard).
  redirects_to_campaigns_after_auth: (state) => CAMPAIGN_LANDING_TYPES.includes(state.type),
}

// mutations
export const mutations = {
  INIT(state, data) {
    if (!data || !data.settings || !data.type) {
      console.warn('User INIT: Called with incomplete data, skipping initialization')
      return
    }

    state.id = data.id
    state.name = data.name
    state.slug = data.slug
    state.email = data.email
    state.avatar = data.settings.company_logo
    state.cpms = data.settings.cpms
    state.type = data.type.slug
    state.parent_user = data?.type?.parent_user || data?.parent ? { ...data.type.parent_user, ...data.parent } : null
    state.publishers = data.publishers ?? null
    state.max_campaigns = data.type.max_campaigns
    state.max_influencers = data.type.max_influencers

    state.impersonate_user_type = data.impersonate_user_type

    state.is_impersonate = data.is_impersonate
    state.gam_network = data?.gam_network?.gam_network_id || null

    state.active_api = data.type.active_api
    state.active_whitelabel = data.type.active_whitelabel
    state.active_ads = data.type.active_ads

    const restrictContentAnalytics = ['brand-admin', 'brand-employee', 'agency-client'].includes(data.type.slug)
    state.active_content = !restrictContentAnalytics
    state.active_analytics = !restrictContentAnalytics

    // SRR-2595: Dashboard access is granted per role, independently of the content/analytics access above.
    const hasPublisherAssigned = Array.isArray(data.publishers) && data.publishers.length > 0
    if (data.type.slug === 'graphic-designer') {
      state.active_dashboard = false
    } else if (data.type.slug === 'brand-admin') {
      // A top-level brand-admin (no parent_user) still sees the Dashboard regardless of this flag —
      // menu.vue's $isTopBrandAdmin() bypasses active_dashboard entirely. This only bites brand-admins with a parent.
      state.active_dashboard = !hasPublisherAssigned
    } else if (data.type.slug === 'agency-client') {
      state.active_dashboard = false
    } else {
      state.active_dashboard = true
    }

    state.demo_features = data.settings.demo_features || false
    state.ai_admin_only = data.ai_admin_only !== undefined ? data.ai_admin_only : true

    state.timezone = data.settings.timezone || 'UTC'
    state.main_message = data.main_message || {}
    state.policy = data.policy || {}
    state.apps_list = Array.isArray(data.apps_list) ? data.apps_list : []
    state.whitelabel_dashboard_id = data.whitelabel_dashboard_id || null
  },

  RESET(state) {
    Object.keys(state).forEach((key) => {
      state[key] = userModel[key]
    })
  },

  [types.SET_GLOBAL_MESSAGE](state, data) {
    state.global_message = data
  },

  [types.UNSET_GLOBAL_MESSAGE](state) {
    state.global_message = {}
  },

  [types.SET_MAIN_MESSAGE](state, data) {
    state.main_message = data
  },

  [types.UNSET_MAIN_MESSAGE](state) {
    state.main_message = {}
  },

  [types.CHANGE_USER_NAME](state, name) {
    state.name = name
  },
}

// actions
export const actions = {}
